import { Avatar, CircularProgress } from "@mui/material";
import Plus from "assets/img/svg/plus";
import type { TArticleData } from "containers/ArticleFormContainer";
import type { ITermFormValues } from "containers/TermFormContainer";
import type { ITitle } from "containers/Titles/types";
import type { ITransferTermFormValues } from "containers/TransferTermFormContainer";
import { formatLocaleDateString } from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { ChangeEvent, HTMLAttributes } from "react";
import type { Control, FieldError, FieldErrorsImpl, Merge } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import AutocompleteFormComponent from "../FormComponent/AutocompleteFormComponent";
import StyledTextField from "../StyledTextField/StyledTextField";

interface ITitleAutocompleteField {
    disabled?: boolean;
    /**
     * Errors specific to field so errors.title for instance.
     */
    errors: Merge<FieldError, FieldErrorsImpl> | undefined;
    control: Control<ITransferTermFormValues> | Control<ITermFormValues> | Control<TArticleData>;
    chosenTitle: ITitle | null | { name: string; id: number };
    controlName: string;
    required?: boolean;
    type?: "title" | "publication";
    /**
     * String to display on the add button. If empty, no add button displayed.
     */
    addButton?: string;
}

/**
 *
 * Autocomplete form component specific to displaying titles.
 */
const TitleAutocompleteField = ({
    disabled,
    errors,
    control,
    controlName,
    chosenTitle,
    required = false,
    type = "title",
    addButton,
}: ITitleAutocompleteField) => {
    const [t] = useTranslation();

    const { companyInfos } = useCompanySettings();
    const publisherType = companyInfos?.publisherType;

    const { titlesAutocomplete } = useAppSelector((state) => state.titles);

    const {
        handleQuery: handleQueryTitle,
        setOffsetPagination: setOffsetPaginationTitles,
        requestStatus: requestStatusTitle,
    } = useAPIPagination({
        type: "titles",
        defaultLimitPagination: 10,
        autocomplete: true,
    });

    const termsLoading = requestStatusTitle === "pending" || requestStatusTitle === "idle";

    const renderInputTitles = (params) => (
        <StyledTextField
            {...params}
            error={!!errors}
            helperText={errors?.message ?? ""}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleQueryTitle(e)}
            InputProps={{
                ...params.InputProps,
                endAdornment: (
                    <>
                        {termsLoading ? (
                            <CircularProgress sx={{ color: variables["primary-purple"] }} size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                    </>
                ),
            }}
        />
    );

    const renderOptionTextTitle = (option: ITitle) => option?.name ?? "";

    const renderOption = (props: HTMLAttributes<HTMLLIElement>, option: ITitle & { addElement?: string }) => {
        return option.addElement ? (
            <li {...props} key={option.id} className="autocomplete-new-element">
                <div className="content">
                    <Plus />
                    {option.addElement}
                </div>
            </li>
        ) : (
            <li
                {...props}
                key={option.id}
                className={`autocomplete-beneficiaries${option.id == chosenTitle?.id ? " selected" : ""}`}
            >
                <div className="autocomplete-beneficiaries-left-content">
                    <Avatar
                        className="autocomplete-avatar"
                        src={option.avatarUrl}
                        variant={"rounded"}
                        sx={{
                            backgroundColor: "#EEF3FD",
                            color: variables["foreground"],
                            fontSize: "14px",
                            height: "40px !important",
                        }}
                    >
                        {(option.name ?? "")?.slice(0, 1)}
                    </Avatar>
                    <span>{`${option.name ?? ""}`}</span>
                </div>
                <div className="autocomplete-beneficiaries-right-content">
                    <span style={{ fontSize: "12px" }}>
                        {type === "title" ? t("ConnectTitle.ISBN") : "ISSN:"} {option?.ean13}
                    </span>
                    {option?.creationDate ? (
                        <span style={{ fontSize: "12px" }}>
                            {t("NewArticle.AddedThe")}{" "}
                            {formatLocaleDateString({
                                date: option.creationDate,
                                options: { numeric: true, timeZone: "UTC" },
                            })}
                        </span>
                    ) : (
                        ""
                    )}
                </div>
            </li>
        );
    };

    const handleOnScrollEnd = () => {
        setOffsetPaginationTitles(titlesAutocomplete?.length ?? 0);
    };

    return (
        <AutocompleteFormComponent
            label={type === "title" ? t("ConnectTitle.LinkedTitle") : t(`NewArticle.Publication_${publisherType}`)}
            options={titlesAutocomplete}
            renderInput={renderInputTitles}
            renderOptionText={renderOptionTextTitle}
            renderOption={renderOption}
            control={control}
            controlName={controlName}
            error={errors}
            required={required}
            customRequiredMessage={
                type === "title" ? t("ConnectTitle.RequiredTitle") : t("Articles.RequiredPublication")
            }
            onScrollEnd={() => handleOnScrollEnd()}
            onScrollEndLoading={requestStatusTitle}
            disabled={disabled}
            loading={termsLoading}
            addElement={addButton}
        />
    );
};

export default TitleAutocompleteField;
